import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import { GraphQLClient } from 'modules/core/lib/graphql/provider'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/metrics'
import trackTransition from 'modules/core/lib/routeTransitionTracking'
import Constant from 'modules/core/store/models/Constant'

import PaymentCompletedModal from 'modules/billing/components/CompletedModal'
import PaymentActionRequiredModal from 'modules/billing/components/PaymentActionRequiredModal'
import PaymentModal from 'modules/billing/components/PaymentModal'
import StripeWrapper from 'modules/billing/components/StripeWrapper'
import { track as googleAnalyticsTrack } from 'modules/billing/lib/googleAnalytics'
import type { Families } from 'modules/billing/store/models/Plan'

interface Props {
  graphQLClient: GraphQLClient | null
  family: Families
  isYearly: boolean
  onClose: () => void
}

type PaymentState = 'pending' | 'action_required' | 'success'

const trackGoogleAdsConversion = (googleAdsConversion: string) => {
  googleAnalyticsTrack('event', 'Purchase', {
    event_category: 'Upgrade Plan',
    event_label: 'Customer',
  })

  googleAnalyticsTrack('event', 'conversion', {
    send_to: googleAdsConversion,
  })
}

const PaymentModalContainer = ({ isYearly, family, onClose, graphQLClient }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [paymentState, setPaymentState] = React.useState<PaymentState>('pending')
  const [googleAdsConversion, setGoogleAdsConversion] = React.useState<string | null>(null)
  const onPaymentActionRequired = useCallback(() => {
    track(graphQLClient, 'PAYMENT_ACTION_REQUIRED')

    setPaymentState('action_required')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const onPaymentSuccessful = useCallback(() => {
    track(graphQLClient, 'PAYMENT_SUCCESSFUL')

    if (googleAdsConversion) {
      trackGoogleAdsConversion(googleAdsConversion)
    }

    setPaymentState('success')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    async function init() {
      trackTransition(graphQLClient, 'payment_modal_view')

      const adwordsData = new Constant({
        id: 'google_adwords_conversion',
      })

      const { value: result } = await asyncAction(() => adwordsData.fetch(), {
        successFlash: false,
        errorFlash: false,
      })

      if (result.value) {
        setGoogleAdsConversion(result.value.adwords_client_conversion)
      }
    }

    init()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (paymentState === 'pending') {
    return (
      <StripeWrapper redirectIfFailRoute={navigation.upgradePlan.fullPath()}>
        <PaymentModal
          isYearly={isYearly}
          family={family}
          onClose={onClose}
          onPaymentActionRequired={onPaymentActionRequired}
          onPaymentSuccessful={onPaymentSuccessful}
        />
      </StripeWrapper>
    )
  }

  if (paymentState === 'action_required') {
    return <PaymentActionRequiredModal />
  }

  if (paymentState === 'success') {
    return (
      <PaymentCompletedModal
        close={() => navigate(navigation.settings.plans.fullPath())}
        cta={i18n.t('billing.completed.cta')}
        description={i18n.t('billing.completed.title')}
        title={i18n.t('billing.completed.pretitle')}
      />
    )
  }

  throw new Error('Invalid payment state')
}

export default observer(PaymentModalContainer)
